import { AppStateType } from '../state/AppState';
import { formatPhoneForDisplay } from '../utils/phoneUtils';

export const PROVIDER_SETUP_INCOMPLETE_TITLE = 'Provider Setup Incomplete';

export function getProviderSetupIncompleteMessage(): string {
  return `The provider has not completed setup. Please call ${formatPhoneForDisplay()}.`;
}

/**
 * Returns a user-facing error message when the /continue response has invalid attributes
 * (e.g. missing profileId), or null when the response is valid.
 */
export function getContinueInvalidAttributeError(continueResponse: any): string | null {
  if (!continueResponse) {
    return null;
  }

  const invalidAttributes = continueResponse.invalidAttributes;
  if (Array.isArray(invalidAttributes)) {
    const hasProfileIssue = invalidAttributes.some(
      (attr: string) => typeof attr === 'string' && attr.toLowerCase() === 'profileid'
    );
    if (hasProfileIssue) {
      return getProviderSetupIncompleteMessage();
    }
  }

  if (continueResponse.success && continueResponse.data) {
    const profileId = continueResponse.data.profileId;
    if (profileId == null || profileId === '' || profileId === 0) {
      return getProviderSetupIncompleteMessage();
    }
  }

  return null;
}

/**
 * SessionSaveData interface for saving application state to remote session endpoint
 */
export interface SessionSaveData {
  // Core navigation state
  providerId: string | null;
  visitType: string | null;
  location: string | null;
  locationCode: string | null;
  locationAddress: string | null;
  service: string | null;
  reason: string | null;
  insurance: string | null;
  appointmentDate: string | null;
  appointmentTime: string | null;
  appointmentDay: string | null;
  
  
  // Regional/state information
  stateString: string | null;
  
  // Profile and column IDs
  profileId: number | null;
  columnId: number | null;
  licenseKey: number | null;
  
  // UTM tracking parameters
  
  // Modalities (from IOI_Payload cs_modalities)
  modalities?: string | null;
  // Age group and client status
  ageGroup: string | null;
  existingClient: string | null;
  relationship: string | null;
  
  // Self-reported source (how user heard about us)
  selfReportedSource?: string | null;
  
  // Available locations
  availableLocations: any[] | null;
  
  // Selected availability for pricing
  selectedAvailability: any | null;
  
  // Timer information
  reservationStartTime: number | null;
  timerStart: string | null;
  
  // Session metadata
  sessionId?: string;
  timestamp: number;
}

/**
 * Session management service for handling session data, query parameters, and state initialization.
 * 
 * This service handles:
 * - Session data fetching and loading
 * - Query parameter processing (mtgSession, contactId)
 * - mtgSession decryption and state mapping
 * - Timer management
 */

/**
 * Get contactId from URL query parameters.
 * 
 * @returns The contactId from the URL query string, or null if not present
 */
export function getContactIdFromQuery(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const contactId = urlParams.get('contactId');
  return contactId;
}

/**
 * Get mtgSession from URL query parameters.
 * 
 * @returns The mtgSession from the URL query string, or null if not present
 */
export function getMtgSessionFromQuery(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const mtgSession = urlParams.get('mtgSession');
  return mtgSession;
}

/**
 * Fetch contact ID from provider code
 * 
 * @param providerCode - The provider code to look up
 * @param baseUrl - The base URL for the API
 * @returns Promise<{ contactId: number, firstName: string, lastName: string } | null>
 */
/**
 * Block an appointment by calling the BlockAppointment API.
 * 
 * @param providerCode - The provider code (5-6 digit code)
 * @param locationState - The region/state
 * @param startDateTime - The appointment start time in ISO format
 * @param baseUrl - The base API URL
 * @returns Promise<boolean> - True if successful, false otherwise
 */
/**
 * Get provider data from the /api/Session/continue endpoint using mtgSession payload.
 * 
 * @param encryptedText - The encrypted text from mtgSession query param
 * @param baseUrl - The base API URL
 * @returns Promise<any> - The provider data response
 */
export async function getProviderData(
  encryptedText: string,
  baseUrl: string
): Promise<any> {
  const requestBody = { encryptedText };
  try {
    const endpoint = `${baseUrl}/api/Session/continue`;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(requestBody)
    });
    if (!response.ok) {
      const errorText = await response.text();
      const dl = (window as any).dataLayer;
      if (dl) {
        dl.push({
          event: 'IOI Error',
          'IOI Error Message': `Invalid Payload: ${JSON.stringify(requestBody)} | HTTP ${response.status}: ${errorText.slice(0, 500)}`
        });
      }
      const error: any = new Error(`HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.responseText = errorText;
      throw error;
    }

    const data = await response.json();
    if (data && data.success === false) {
      const dl = (window as any).dataLayer;
      if (dl) {
        dl.push({
          event: 'IOI Error',
          'IOI Error Message': `Invalid Payload: ${JSON.stringify(requestBody)} | ${JSON.stringify(data).slice(0, 1000)}`
        });
      }
    }
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Create a patient by calling the Patient/create API.
 * 
 * @param patientData - The patient data to create
 * @param baseUrl - The base API URL
 * @returns Promise<string | null> - Patient ID if successful, null otherwise
 */
export async function createPatient(
  patientData: {
    name: string;
    profile: string;
    sendApptReminderText: boolean;
    sex: string;
    relationship: string;
    dob: string;
    ssn: string;
    chart: string;
    selfReportedSource?: string | null;
    marketingSource?: string | null;
    address: {
      zip: string;
      city: string;
      state: string;
      address2: string;
    };
    contactInfo: {
      homePhone: string;
      otherPhone: string;
      otherType: string;
      email: string;
      preferredCommunicationFid: string;
    };
    responsibleParty?: {
      name: string;
      dob: string;
      address: {
        address1: string;
        city: string;
        state: string;
        zip: string;
      };
      contactInfo?: {
        otherPhone?: string;
        otherType?: string;
        email?: string;
      };
    };
    insurances?: Array<{
      carrierName: string;
      subscriberNum: string;
    }>;
  },
  baseUrl: string
): Promise<{ success: boolean; patientId?: string; error?: string; errorCode?: string }> {
  try {
    const endpoint = `${baseUrl}/api/Patient/create`;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(patientData)
    });
    // Parse the response regardless of status code
    const data = await response.json();
    // Check for errors in the response body first
    if (data.error) {
      // Check for specific error code -2147217400 (duplicate patient)
      if (data.error === "-2147217400") {
        return {
          success: false,
          error: "A patient with the given name and date of birth exists in our system.",
          errorCode: "-2147217400"
        };
      }
      
      return {
        success: false,
        error: data.error || data.message || "Failed to create patient",
        errorCode: data.error
      };
    }
    
    // If no error in response body, check HTTP status
    if (!response.ok) {
      return {
        success: false,
        error: "Failed to create patient",
        errorCode: "HTTP_ERROR"
      };
    }
    
    // Extract patient ID from response
    const patientId = data.patient?.id || data.patientId || data.id || data.patient_id;
    if (patientId) {
      return {
        success: true,
        patientId: patientId.toString()
      };
    } else {
      return {
        success: false,
        error: "No patient ID found in response",
        errorCode: "NO_PATIENT_ID"
      };
    }
  } catch (error) {
    return {
      success: false,
      error: "Failed to create patient",
      errorCode: "NETWORK_ERROR"
    };
  }
}

/**
 * Upload an image by calling the Session/upload-image API.
 * 
 * @param filename - The filename for the uploaded image
 * @param description - Description of the image
 * @param base64Content - Base64 encoded image content
 * @param baseUrl - The base API URL
 * @returns Promise<boolean> - True if successful, false otherwise
 */
export async function uploadImage(
  filename: string,
  description: string,
  base64Content: string,
  baseUrl: string
): Promise<boolean> {
  try {
    const endpoint = `${baseUrl}/api/Session/upload-image`;
    const requestBody = {
      filename: filename,
      description: description,
      base64Content: base64Content
    };
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Book an appointment by calling the BookAppointment API.
 * 
 * @param providerCode - The provider code (5-6 digit code)
 * @param providerName - The provider name
 * @param locationState - The region/state
 * @param startDateTime - The appointment start time in ISO format
 * @param patientId - The patient ID returned from patient creation
 * @param profileId - The profile ID from session data
 * @param columnId - The column ID from session data
 * @param relationship - The relationship to primary subscriber (e.g., 'self', 'spouse', 'child')
 * @param baseUrl - The base API URL
 * @param locationCode - The location code (e.g., '0000' or 'telehealth')
 * @param availabilityId - Optional availability ID
 * @returns Promise<boolean> - True if successful, false otherwise
 */
export async function bookAppointment(
  providerCode: string,
  providerName: string,
  locationState: string,
  startDateTime: string,
  patientId: string,
  profileId: number | null,
  columnId: number | null,
  relationship: string | null,
  baseUrl: string,
  locationCode: string | null,
  availabilityId?: number | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const endpoint = `${baseUrl}/api/IoiApi/BookAppointment`;
    const requestBody: any = {
      providerCode: providerCode,
      locationCode: locationCode ?? '0000',
      providerName: providerName,
      locationState: locationState,
      startDateTime: startDateTime,
      patientId: patientId,
      profileId: profileId,
      columnId: columnId,
      relationship: relationship,
      durationMinutes: 60,
      appointmentType: "Consultation"
    };
    
    // Add availabilityId if provided
    if (availabilityId !== null && availabilityId !== undefined) {
      requestBody.availabilityId = availabilityId;
    }
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(requestBody)
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const errorMsg = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      return { success: false, error: errorMsg };
    }
    
    return { success: true };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    return { success: false, error: errorMsg };
  }
}

export async function blockAppointment(
  providerCode: string,
  contactId: string,
  columnId: number | null,
  profileId: number | null,
  providerName: string,
  locationState: string,
  startDateTime: string,
  baseUrl: string,
  availabilityId?: number | null
): Promise<boolean> {
  try {
    const endpoint = `${baseUrl}/api/IoiApi/BlockAppointment`;
    const requestBody: any = {
      providerCode: providerCode,
      contactId: contactId,
      columnId: columnId,
      profileId: profileId,
      providerName: providerName,
      locationState: locationState,
      startDateTime: startDateTime,
      durationMinutes: 60,
      forView: "day",
      reasonId: "CONSULTATION",
      noteText: "Initial appointment"
    };
    
    // Add availabilityId if provided
    if (availabilityId !== null && availabilityId !== undefined) {
      requestBody.availabilityId = availabilityId;
    }
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(requestBody)
    });
    if (!response.ok) {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Fetch blocked timeslots by calling the IoiApi scheduler/exclusions API.
 * Response body is a JSON array of exclusion records (same item shape as before).
 *
 * @param columnId - The column ID
 * @param forView - The view type (e.g., "week")
 * @param startDate - The start date in YYYY-MM-DD format
 * @param baseUrl - The base API URL
 * @param endDate - Optional end date in YYYY-MM-DD format (same as startDate for single-day queries)
 * @returns Promise<Array<any>> - Array of blocked timeslots, or empty array on error
 */
export async function fetchBlockHolds(
  columnId: number | null,
  forView: string,
  startDate: string,
  baseUrl: string,
  endDate?: string
): Promise<Array<any>> {
  try {
    if (!columnId) {
      return [];
    }
    
    let endpoint = `${baseUrl}/api/IoiApi/scheduler/exclusions?columnId=${columnId}&forView=${forView}&startDate=${startDate}&verbose=false`;
    if (endDate) {
      endpoint += `&endDate=${endDate}`;
    }
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    return [];
  }
}

export async function fetchContactFromProviderCode(providerCode: string, baseUrl: string, dispatch: (action: any) => void): Promise<{ contactId: number, firstName: string, lastName: string } | null> {
  try {
    const response = await fetch(`${baseUrl}/api/provider/contact/${providerCode}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });
    
    
    if (!response.ok) {
      return null;
    }
    
    // Get raw text first to see what we're actually receiving
    const rawText = await response.text();
    // Check if there are any hidden characters or encoding issues
    
    
    const data = JSON.parse(rawText);
    if (data.success && data.contactId) {
      
      dispatch({ type: 'SET_PROVIDER_ID', payload: data.contactId.toString() });
      
      // Force session save immediately after setting contactId
      setTimeout(async () => {
        try {
          // Create a minimal session data object with just the providerId
          const sessionData = {
            providerId: data.contactId.toString(),
            visitType: null,
            location: null,
            locationCode: null,
            locationAddress: null,
            service: null,
            reason: null,
            insurance: null,
            appointmentDate: null,
            appointmentTime: null,
            appointmentDay: null,
            reservationStartTime: null,
            ageGroup: null,
            existingClient: null,
            preferredProvider: null,
            preferredProviderCode: null,
            providerHeadshot: null,
            stateString: null,
            columnId: null,
            profileId: null,
            licenseKey: null,
            utmCampaign: null,
            utmSource: null,
            utmMedium: null,
            utmTerm: null,
            utmContent: null,
            availabilities: null,
            availableLocations: null,
            selectedAvailability: null,
            sessionData: null,
            mtgSession: null,
            remoteApiUrl: null,
            uploadedImages: null,
            timerWarningModalDismissed: false,
            isLoading: false,
            loadingStep: 'complete',
            loadingProgress: 100,
            phone: null,
            relationship: null,
            selfReportedSource: null,
            textConsent: null,
            modalities: null
          };
          await saveSessionToRemote(sessionData, baseUrl);
        } catch (error) {
        }
      }, 100);
      return {
        contactId: data.contactId,
        firstName: data.firstName || '',
        lastName: data.lastName || ''
      };
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Fetch session data from the API.
 * 
 * Retrieves session information from the /api/Session/all endpoint.
 * 
 * @param baseUrl - Base URL for the API (e.g., "http://localhost:5293")
 * @returns Promise<any> - Session data from the API
 * 
 * @example
 * ```typescript
 * const sessionData = await fetchSessionData('http://localhost:5293');
 * ```
 */
export async function fetchSessionData(baseUrl: string): Promise<any> {
  try {
    const response = await fetch(`${baseUrl}/api/Session/all`, {
      credentials: 'include'
    });
    
    
    if (!response.ok) {
      return null;
    }
    
    const sessionData = await response.json();
    return sessionData;
  } catch (error) {
    return null;
  }
}

/**
 * Set timer start time via the API.
 * 
 * Sends the timer start time to the /api/Session/set/timerStart endpoint.
 * 
 * @param baseUrl - Base URL for the API (e.g., "http://localhost:5293")
 * @param timerStartTime - Timer start time as ISO string
 * @returns Promise<void>
 * 
 * @example
 * ```typescript
 * await setTimerStart('http://localhost:5293', new Date().toISOString());
 * ```
 */
export async function setTimerStart(baseUrl: string, timerStartTime: string): Promise<void> {
  try {
    const response = await fetch(`${baseUrl}/api/Session/set/timerStart`, {
      method: 'POST',
      credentials: 'include',
      body: timerStartTime
    });
    if (!response.ok) {
    } else {
    }
  } catch (error) {
  }
}

/**
 * Clear block holds for the current session.
 * 
 * Calls the POST endpoint to remove any appointment block holds.
 * This is typically called when the user navigates away from the appointment confirmation screen.
 * 
 * @param baseUrl - Base URL for the API (e.g., "http://localhost:5293")
 * @returns Promise<void>
 * 
 * @example
 * ```typescript
 * clearBlockHolds('http://localhost:5293');
 * ```
 */
export async function clearBlockHolds(baseUrl: string): Promise<void> {
  try {
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const endpoint = `${normalizedBaseUrl}api/Session/clear-block-holds`;
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
    } else {
    }
  } catch (error) {
  }
}

/**
 * Load session data into app state.
 * 
 * Parses session data and dispatches appropriate actions to populate the app state.
 * 
 * @param sessionData - Session data from the API
 * @param dispatch - Dispatch function to update app state
 */
export function loadSessionDataIntoState(sessionData: any, dispatch: (action: any) => void): void {
  if (!sessionData || !sessionData.data) {
    return;
  }
  // The session data is nested - we need to parse the inner data field
  let sessionDataObj;
  if (sessionData.data.data && typeof sessionData.data.data === 'string') {
    // Parse the JSON string from the nested data field
    sessionDataObj = JSON.parse(sessionData.data.data);
  } else {
    // Fallback to direct data field
    sessionDataObj = sessionData.data;
  }
  
  // Store current selectedAvailability before loading session data
  const currentSelectedAvailability = sessionDataObj.selectedAvailability;
  // Load all session fields into app state
  if (sessionDataObj.providerId) dispatch({ type: 'SET_PROVIDER_ID', payload: sessionDataObj.providerId });
  if (sessionDataObj.visitType) dispatch({ type: 'SET_VISIT_TYPE', payload: sessionDataObj.visitType });
  if (sessionDataObj.location) dispatch({ type: 'SET_LOCATION', payload: sessionDataObj.location });
  if (sessionDataObj.locationCode) dispatch({ type: 'SET_LOCATION_CODE', payload: sessionDataObj.locationCode });
  if (sessionDataObj.locationAddress) dispatch({ type: 'SET_LOCATION_ADDRESS', payload: sessionDataObj.locationAddress });
  if (sessionDataObj.service) dispatch({ type: 'SET_SERVICE', payload: sessionDataObj.service });
  if (sessionDataObj.reason) dispatch({ type: 'SET_REASON', payload: sessionDataObj.reason });
  if (sessionDataObj.insurance) dispatch({ type: 'SET_INSURANCE', payload: sessionDataObj.insurance });
  if (sessionDataObj.appointmentDate) dispatch({ type: 'SET_APPOINTMENT_DATE', payload: sessionDataObj.appointmentDate });
  if (sessionDataObj.appointmentTime) dispatch({ type: 'SET_APPOINTMENT_TIME', payload: sessionDataObj.appointmentTime });
  if (sessionDataObj.appointmentDay) dispatch({ type: 'SET_APPOINTMENT_DAY', payload: sessionDataObj.appointmentDay });
  if (sessionDataObj.preferredProvider) dispatch({ type: 'SET_PREFERRED_PROVIDER', payload: sessionDataObj.preferredProvider });
  if (sessionDataObj.preferredProviderCode) dispatch({ type: 'SET_PREFERRED_PROVIDER_CODE', payload: sessionDataObj.preferredProviderCode });
  if (sessionDataObj.providerHeadshot) dispatch({ type: 'SET_PROVIDER_HEADSHOT', payload: sessionDataObj.providerHeadshot });
  if (sessionDataObj.stateString) dispatch({ type: 'SET_STATE_STRING', payload: sessionDataObj.stateString });
  if (sessionDataObj.profileId) dispatch({ type: 'SET_PROFILE_ID', payload: sessionDataObj.profileId });
  if (sessionDataObj.columnId) dispatch({ type: 'SET_COLUMN_ID', payload: sessionDataObj.columnId });
  if (sessionDataObj.licenseKey) dispatch({ type: 'SET_LICENSE_KEY', payload: sessionDataObj.licenseKey });
  if (sessionDataObj.ageGroup) dispatch({ type: 'SET_AGE_GROUP', payload: sessionDataObj.ageGroup });
  if (sessionDataObj.existingClient) dispatch({ type: 'SET_EXISTING_CLIENT', payload: sessionDataObj.existingClient });
  if (sessionDataObj.availableLocations) dispatch({ type: 'SET_AVAILABLE_LOCATIONS', payload: sessionDataObj.availableLocations });
  if (currentSelectedAvailability) {
    dispatch({ type: 'SET_SELECTED_AVAILABILITY', payload: currentSelectedAvailability });
  } else {
  }
  
  // UTM parameters
  if (sessionDataObj.utmCampaign) dispatch({ type: 'SET_UTM_CAMPAIGN', payload: sessionDataObj.utmCampaign });
  if (sessionDataObj.utmSource) dispatch({ type: 'SET_UTM_SOURCE', payload: sessionDataObj.utmSource });
  if (sessionDataObj.utmMedium) dispatch({ type: 'SET_UTM_MEDIUM', payload: sessionDataObj.utmMedium });
  if (sessionDataObj.utmTerm) dispatch({ type: 'SET_UTM_TERM', payload: sessionDataObj.utmTerm });
  if (sessionDataObj.utmContent) dispatch({ type: 'SET_UTM_CONTENT', payload: sessionDataObj.utmContent });
  if (sessionDataObj.modalities != null && sessionDataObj.modalities !== '') dispatch({ type: 'SET_MODALITIES', payload: sessionDataObj.modalities });
  
  // Initialize timer if timerStart is set in session data
  // Check both the parsed session data and the outer session data for timerStart
  const timerStart = sessionDataObj.timerStart || sessionData.data.timerStart;
  if (timerStart) {
    const timerStartTime = new Date(timerStart).getTime();
    dispatch({ type: 'SET_RESERVATION_START_TIME', payload: timerStartTime });
  } else {
  }
}

/**
 * Process query parameters and return the resolved provider ID.
 * 
 * Handles the priority order: mtgSession → contactId → session data
 * 
 * @param baseUrl - Base URL for the API
 * @param dispatch - Dispatch function to update app state
 * @param sessionData - Session data from the API
 * @returns Promise<string | null> - Resolved provider ID or null
 */
export async function processQueryParameters(
  baseUrl: string, 
  dispatch: (action: any) => void, 
  sessionData: any
): Promise<string | null> {
  // Get query parameters
  const mtgSessionFromQuery = getMtgSessionFromQuery();
  // 1. If mtgSession is provided, call /api/Data endpoint and set data in session
  if (mtgSessionFromQuery) {
    try {
      // Reset session before processing mtgSession
      dispatch({ type: 'RESET' });
      // Call the new /api/Data endpoint
      const providerData = await getProviderData(mtgSessionFromQuery, baseUrl);
      const setupError = getContinueInvalidAttributeError(providerData);
      if (setupError) {
        throw new Error(setupError);
      }
      if (providerData.success && providerData.data) {
        const data = providerData.data;
        const originalData = providerData.originalData;
        // Set the core provider data in session - prioritize originalData values
        let contactId = data.contactId;
        let firstName = data.firstName;
        let lastName = data.lastName;
        let locationState = data.locationState;
        let providerCode = data.providerCode;
        
        // Override with originalData values if available
        if (originalData && originalData.IOI_Payload) {
          const payload = originalData.IOI_Payload;
          
          if (payload.cs_contactId) {
            contactId = payload.cs_contactId;
          }
          
          if (payload.cs_preferredFullName) {
            const decodedName = decodeURIComponent(payload.cs_preferredFullName);
            const nameParts = decodedName.split('+');
            if (nameParts.length >= 2) {
              firstName = nameParts[0];
              lastName = nameParts[1];
            }
          }
          
          if (payload.cs_state) {
            locationState = decodeURIComponent(payload.cs_state);
          }
          
          if (payload.cs_providerCode) {
            providerCode = payload.cs_providerCode;
          }
        }
        
        // Set the resolved values in session
        if (contactId) {
          dispatch({ type: 'SET_PROVIDER_ID', payload: contactId.toString() });
        }
        
        if (firstName && lastName) {
          dispatch({ type: 'SET_PREFERRED_PROVIDER', payload: firstName + ' ' + lastName });
        }
        
        if (locationState) {
          dispatch({ type: 'SET_STATE_STRING', payload: locationState });
        }
        
        if (providerCode) {
          dispatch({ type: 'SET_PREFERRED_PROVIDER_CODE', payload: providerCode });
        }
        
        // Set additional provider data if available
        if (data.providerHeadshot) {
          dispatch({ type: 'SET_PROVIDER_HEADSHOT', payload: data.providerHeadshot });
        }
        
        // Store additional metadata in state
        if (data.columnId) {
          dispatch({ type: 'SET_COLUMN_ID', payload: data.columnId });
        }
        
        if (data.profileId) {
          dispatch({ type: 'SET_PROFILE_ID', payload: data.profileId });
        }
        
        if (data.licenseKey) {
          dispatch({ type: 'SET_LICENSE_KEY', payload: data.licenseKey });
        }
        
        if (data.columnHeading) {
          // Store the column heading for reference
        }
        
        // Map new fields to existing state attributes
        if (data.insurances) {
          dispatch({ type: 'SET_INSURANCE', payload: data.insurances });
        }
        
        if (data.specialties) {
          dispatch({ type: 'SET_REASON', payload: data.specialties });
        }
        
        if (data.ageRange) {
          dispatch({ type: 'SET_AGE_GROUP', payload: data.ageRange });
        }
        
        if (data.service) {
          dispatch({ type: 'SET_SERVICE', payload: data.service });
        }
        
        if (data.phone) {
          dispatch({ type: 'SET_PHONE', payload: data.phone });
        }
        if (data.textConsent !== undefined) {
          dispatch({ type: 'SET_TEXT_CONSENT', payload: data.textConsent });
        }
        
        // Set data from originalData.IOI_Payload if available
        if (originalData && originalData.IOI_Payload) {
          const payload = originalData.IOI_Payload;
          
          // Set location data
          if (payload.cs_location) {
            const decodedLocation = decodeURIComponent(payload.cs_location);
            dispatch({ type: 'SET_LOCATION', payload: decodedLocation });
          }
          
          if (payload.cs_locationCode) {
            const decodedLocationCode = decodeURIComponent(payload.cs_locationCode);
            dispatch({ type: 'SET_LOCATION_CODE', payload: decodedLocationCode });
          }
          
          if (payload.cs_state) {
            const decodedState = decodeURIComponent(payload.cs_state);
            dispatch({ type: 'SET_STATE_STRING', payload: decodedState });
          }
          
          // Set appointment time
          if (payload.cs_time) {
            const decodedTime = decodeURIComponent(payload.cs_time);
            dispatch({ type: 'SET_APPOINTMENT_TIME', payload: decodedTime });
          }
          
          // Set appointment day
          if (payload.cs_day) {
            const decodedDay = decodeURIComponent(payload.cs_day);
            dispatch({ type: 'SET_APPOINTMENT_DAY', payload: decodedDay });
          }
          
          // Set UTM tracking data
          if (payload.cs_campaign) {
            const decodedCampaign = decodeURIComponent(payload.cs_campaign);
            dispatch({ type: 'SET_UTM_CAMPAIGN', payload: decodedCampaign });
          }
          
          if (payload.cs_source) {
            const decodedSource = decodeURIComponent(payload.cs_source);
            dispatch({ type: 'SET_UTM_SOURCE', payload: decodedSource });
          }
          
          if (payload.cs_medium) {
            const decodedMedium = decodeURIComponent(payload.cs_medium);
            dispatch({ type: 'SET_UTM_MEDIUM', payload: decodedMedium });
          }
          
          if (payload.cs_modalities != null && payload.cs_modalities !== '') {
            const modalities = typeof payload.cs_modalities === 'string'
              ? decodeURIComponent(payload.cs_modalities)
              : String(payload.cs_modalities);
            dispatch({ type: 'SET_MODALITIES', payload: modalities });
          }
          
          if (payload.cs_referrer) {
            // You might want to store this in state if needed
          }
          
          if (payload.cs_mClid) {
            // You might want to store this in state if needed
          }
          
          if (payload.cs_usrAgent) {
            // You might want to store this in state if needed
          }
        }
        
        // Note: IOI_Payload is now handled by the backend and not stored in app state
        return contactId ? contactId.toString() : null;
      } else {
        throw new Error('Failed to continue session from /api/Session/continue endpoint');
      }
    } catch (error) {
      throw error; // Don't continue if mtgSession processing fails
    }
  }
  
  // 2. If no mtgSession, load from existing session data
  if (sessionData && sessionData.data) {
    loadSessionDataIntoState(sessionData, dispatch);
    
    // Extract providerId from session data
    let sessionDataObj;
    if (sessionData.data.data && typeof sessionData.data.data === 'string') {
      sessionDataObj = JSON.parse(sessionData.data.data);
    } else {
      sessionDataObj = sessionData.data;
    }
    
    if (sessionDataObj.providerId) {
      return sessionDataObj.providerId;
    }
  }
  return null;
}

/**
 * Filters out sensitive form data from ScreenP and other form fields
 * that should not be saved to the session
 */
function filterSessionData(state: AppStateType): SessionSaveData {
  return {
    // Core navigation state
    providerId: state.providerId,
    visitType: state.visitType,
    location: state.location,
    locationCode: state.locationCode,
    locationAddress: state.locationAddress,
    service: state.service,
    reason: state.reason,
    insurance: state.insurance,
    appointmentDate: state.appointmentDate,
    appointmentTime: state.appointmentTime,
    appointmentDay: state.appointmentDay,
    
    
    // Regional/state information
    stateString: state.stateString,
    
    // Modalities (from IOI_Payload cs_modalities)
    modalities: state.modalities,
    
    // Profile and column IDs
    profileId: state.profileId,
    columnId: state.columnId,
    licenseKey: state.licenseKey,
    
    
    // Age group and client status
    ageGroup: state.ageGroup,
    existingClient: state.existingClient,
    relationship: state.relationship,
    selfReportedSource: state.selfReportedSource,
    
    // Available locations
    availableLocations: state.availableLocations,
    
    // Selected availability for pricing
    selectedAvailability: state.selectedAvailability,
    
    // Timer information
    reservationStartTime: state.reservationStartTime,
    timerStart: state.reservationStartTime ? new Date(state.reservationStartTime).toISOString() : null,
    
    // Session metadata
    timestamp: Date.now()
  };
}

/**
 * Saves the current application state to the remote session endpoint
 * 
 * @param state - The current application state
 * @param remoteApiUrl - The base URL for the remote API
 * @returns Promise<boolean> - True if save was successful, false otherwise
 */
export async function saveSessionToRemote(
  state: AppStateType, 
  remoteApiUrl: string | null
): Promise<boolean> {
  try {
    if (!remoteApiUrl) {
      return false;
    }

    const sessionData = filterSessionData(state);
    const endpoint = `${remoteApiUrl}/api/Session/set/data`;
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(sessionData)
    });

    if (!response.ok) {
      return false;
    }
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Saves session data with error handling and logging
 * This is the main function to call from components
 * 
 * @param state - The current application state
 * @param remoteApiUrl - The base URL for the remote API
 * @param context - Optional context string for logging (e.g., "ScreenD continue button")
 */
export async function saveSession(
  state: AppStateType, 
  remoteApiUrl: string | null,
  context?: string
): Promise<void> {
  if (!remoteApiUrl) {
    return;
  }
  
  const success = await saveSessionToRemote(state, remoteApiUrl);
  
  if (!success) {
  }
}
